import { useState, useEffect, useCallback } from 'react';
import { Box, Button, Container, Heading, Icon, Stack, StackSeparator, Text } from '@chakra-ui/react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams, useLoaderData } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { ContentType, BlockType, FieldOption, StaticField } from '@/lib/types';
import FormContent from './form-content';
import { STATIC_FIELDS } from '@/lib/constants';

interface ContentTypeField {
  id: number;
  referenceID: string;
  field_id: string;
  field_name: string;
  field_type: number;
  field_unique_name: string;
  field_options: FieldOption[];
  workspace_id: number;
  content_type_id: number;
  sort_order: number;
  properties: any;
}

const processSelectionField = (field: ContentTypeField): ContentTypeField => {
  const fieldCopy = { ...field };

  if (field.field_type === BlockType.Selection) {
    fieldCopy.properties = { ...fieldCopy.properties, options: [] };
    const sortedOptions = [...field.field_options].sort(
      (a: FieldOption, b: FieldOption) => a.sort_order - b.sort_order,
    );

    fieldCopy.properties.options = sortedOptions.map((item: FieldOption) => ({
      label: item.option_label,
      value: item.option_value,
    }));
  }

  return fieldCopy;
};

const processStaticField = (field: ContentTypeField): ContentTypeField => {
  const fieldCopy = { ...field };

  if (field.field_unique_name && STATIC_FIELDS.includes(field.field_unique_name as StaticField)) {
    fieldCopy.referenceID = field.field_unique_name;
  }

  return fieldCopy;
};

export default function CreateContent() {
  const fields = useLoaderData() as ContentTypeField[];
  const navigate = useNavigate();
  const [rootBlocks, setRootBlocks] = useState<ContentTypeField[]>([]);
  const [contentType, setContentType] = useState<ContentType>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { contentTypeId } = useParams<{
    contentTypeId: string;
  }>();

  const fetchContentType = useCallback(async () => {
    if (!contentTypeId) return;

    setIsLoading(true);

    try {
      const response = await api.get(`content-types/${contentTypeId}`);
      const fetchedContentType = response.data.data;
      setContentType(fetchedContentType);

      const processedFields = fields.map((field) => {
        let processedField = processSelectionField(field);
        processedField = processStaticField(processedField);

        return processedField;
      });

      const filteredFields = processedFields.filter((field) => {
        return !['s_created_date', 's_is_public'].includes(field.field_unique_name);
      });

      const sortedFields = [...filteredFields].sort((a, b) => a.sort_order - b.sort_order);

      const staticFieldsList: ContentTypeField[] = [];
      const sortableFieldsList: ContentTypeField[] = [];

      sortedFields.forEach((field: ContentTypeField) => {
        if (STATIC_FIELDS.includes(field.field_unique_name as StaticField)) {
          staticFieldsList.push(field);
        } else {
          sortableFieldsList.push(field);
        }
      });

      setRootBlocks([...staticFieldsList, ...sortableFieldsList]);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to load content type',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [contentTypeId, fields]);

  useEffect(() => {
    fetchContentType();
  }, [fetchContentType]);

  const typeName = contentType?.name || 'Content';

  return (
    <Container maxW='4xl' py={{ base: 4, md: 6 }} px={{ base: 4, md: 6 }}>
      <Stack mb={6} gap={2} align='flex-start'>
        <Button variant='ghost' size='sm' onClick={() => navigate(-1)}>
          <Icon as={ArrowLeftIcon} w={4} h={4} />
          Back
        </Button>
        <Heading
          as='h1'
          fontSize={{ base: '2xl', md: '3xl' }}
          fontWeight='bold'
          letterSpacing='-0.01em'
        >
          Create {typeName}
        </Heading>
        {contentType?.name && (
          <Text color='fg.muted' fontSize='sm'>
            Add a new {typeName.toLowerCase()} to your library.
          </Text>
        )}
      </Stack>

      <Box
        bg='bg.panel'
        borderWidth={1}
        borderColor='border'
        borderRadius='2xl'
        shadow='xs'
        p={{ base: 5, md: 8 }}
      >
        {isLoading ? (
          <Stack gap={5}>
            <Skeleton height='10' />
            <Skeleton height='6' width='40%' />
            <Skeleton height='32' />
            <Skeleton height='10' />
            <Skeleton height='24' />
          </Stack>
        ) : (
          <Stack gap={4} separator={<StackSeparator />}>
            <FormContent
              rootBlocks={rootBlocks}
              responses={{}}
              options={{
                contentTypeName: contentType?.name || '',
              }}
            />
          </Stack>
        )}
      </Box>
    </Container>
  );
}
